import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import {
  BarChart3, TrendingUp, Activity, Loader2, RefreshCw,
  AlertTriangle, Shield, Eye, FileText, Building2,
  Target, Flame, ChevronDown, ChevronUp, Layers
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

const CHART_COLORS = ["#36A9E1", "#22C55E", "#F59E0B", "#EF4444", "#A855F7", "#EC4899", "#06B6D4", "#84CC16"];
const SEVERITY_COLORS_MAP = {
  observation: "#60A5FA",
  ofi: "#22D3EE",
  minor_nonconformance: "#FBBF24",
  major_nonconformance: "#F97316",
  critical_nonconformance: "#EF4444",
  unknown: "#6B7280",
};
const HEAT_COLORS = { critical: "#EF4444", high: "#F97316", medium: "#FBBF24", low: "#22C55E" };
const STATUS_COLORS = {
  draft: "#6B7280", submitted: "#60A5FA", under_review: "#FBBF24",
  approved: "#22C55E", rejected: "#EF4444", inactive: "#9CA3AF",
  pending: "#F59E0B", completed: "#22C55E", applied: "#36A9E1",
};

const CustomTooltip = ({ active, payload, label }) => {
  const { t } = useTranslation('platform');
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1F222D] border border-[#2A2D38] rounded-lg p-3 shadow-xl">
      <p className="text-xs text-[#8B95A5] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm" style={{ color: p.color || p.fill }}>
          {p.name}: <span className="font-semibold text-white">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const KPICard = ({ label, value, icon: Icon, color, sub }) => (
  <div className="bg-[#1F222D] border border-[#2A2D38] rounded-lg p-4 hover:border-[#36A9E1]/20 transition-colors" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg bg-[#181B23]`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <div className="text-2xl font-bold text-white">{value ?? "—"}</div>
        <div className="text-xs text-[#8B95A5]">{label}</div>
        {sub && <div className="text-xs text-[#8B95A5] mt-0.5">{sub}</div>}
      </div>
    </div>
  </div>
);

const ChartPanel = ({ title, icon: Icon, color, children, id }) => (
  <div className="bg-[#1F222D] border border-[#2A2D38] rounded-lg overflow-hidden" data-testid={`chart-${id}`}>
    <div className="px-5 py-3 border-b border-[#2A2D38] flex items-center gap-2">
      <Icon className={`w-4 h-4 ${color}`} />
      <h3 className="text-sm font-semibold text-white">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const EmptyChart = ({ message }) => (
  <div className="h-[250px] flex items-center justify-center text-[#8B95A5] text-sm">
    {message || "No data available"}
  </div>
);

/* ═════════════════════════════════════════════════════════════════ */
const AnalyticsChartsPage = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [findingsTrend, setFindingsTrend] = useState(null);
  const [clauseHeatmap, setClauseHeatmap] = useState(null);
  const [severityDist, setSeverityDist] = useState(null);
  const [recurrenceTrend, setRecurrenceTrend] = useState(null);
  const [vendorTrends, setVendorTrends] = useState(null);
  const [redactionMetrics, setRedactionMetrics] = useState(null);
  const [disclosureCompliance, setDisclosureCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("all");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ft, ch, sd, rt, vt, rm, dc] = await Promise.all([
        ax.get(`${API}/analytics/overview`).then(r => r.data).catch(() => null),
        ax.get(`${API}/analytics/findings-trend?bucket=week`).then(r => r.data).catch(() => null),
        ax.get(`${API}/analytics/clause-heatmap`).then(r => r.data).catch(() => null),
        ax.get(`${API}/analytics/severity-distribution`).then(r => r.data).catch(() => null),
        ax.get(`${API}/analytics/recurrence-trend?bucket=week`).then(r => r.data).catch(() => null),
        ax.get(`${API}/analytics/vendor-trends`).then(r => r.data).catch(() => null),
        ax.get(`${API}/analytics/redaction-metrics`).then(r => r.data).catch(() => null),
        ax.get(`${API}/analytics/disclosure-compliance`).then(r => r.data).catch(() => null),
      ]);
      setOverview(ov); setFindingsTrend(ft); setClauseHeatmap(ch);
      setSeverityDist(sd); setRecurrenceTrend(rt); setVendorTrends(vt);
      setRedactionMetrics(rm); setDisclosureCompliance(dc);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) {
    return (
      <QMSLayout title={t('platform:analytics.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  const kpis = overview?.kpis || {};

  const sections = [
    { id: "all", label: "All Charts" },
    { id: "findings", label: "Findings" },
    { id: "vendor", label: "Vendors" },
    { id: "compliance", label: "Compliance" },
  ];

  return (
    <QMSLayout title={t('platform:analytics.title')}>
      <div className="space-y-6" data-testid="analytics-charts-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-[#36A9E1]" />
              P3 Analytics Dashboard
            </h1>
            <p className="text-sm text-[#8B95A5] mt-1">Cross-module chart visualizations</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-[#181B23] rounded-lg p-1">
              {sections.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  className={`px-3 py-1.5 rounded text-xs transition-colors ${
                    section === s.id ? "bg-[#36A9E1]/10 text-[#36A9E1]" : "text-[#8B95A5] hover:text-white"
                  }`}
                  data-testid={`section-${s.id}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <button
              onClick={loadAll}
              className="p-2 rounded-lg bg-[#181B23] text-[#8B95A5] hover:text-[#36A9E1] transition-colors"
              data-testid="refresh-analytics"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="kpi-strip">
          <KPICard label="Total Findings" value={kpis.total_findings} icon={AlertTriangle} color="text-amber-400" />
          <KPICard label="Critical" value={kpis.critical_findings} icon={Flame} color="text-red-400" />
          <KPICard label="Open SCARs" value={kpis.open_scars} icon={Target} color="text-orange-400" sub={`${kpis.total_scars || 0} total`} />
          <KPICard label="Active Audits" value={kpis.active_audits} icon={Shield} color="text-[#36A9E1]" sub={`${kpis.total_audits || 0} total`} />
          <KPICard label="Vendors" value={kpis.approved_vendors} icon={Building2} color="text-emerald-400" sub={`${kpis.total_vendors || 0} total`} />
          <KPICard label="Recurrences" value={kpis.active_recurrences} icon={Activity} color="text-purple-400" />
        </div>

        {/* Chart Grid */}
        {(section === "all" || section === "findings") && (
          <>
            {/* Findings Trend */}
            <ChartPanel title={t('platform:analytics.findingsTrend')} icon={TrendingUp} color="text-[#36A9E1]" id="findings-trend">
              {findingsTrend?.overall?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={findingsTrend.overall}>
                    <defs>
                      <linearGradient id="findingsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#36A9E1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#36A9E1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2A2D38" />
                    <XAxis dataKey="period" tick={{ fill: "#8B95A5", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#8B95A5", fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="count" stroke="#36A9E1" fill="url(#findingsGrad)" name="Findings" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <EmptyChart />}
            </ChartPanel>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Severity Distribution */}
              <ChartPanel title={t('platform:analytics.severityDist')} icon={AlertTriangle} color="text-amber-400" id="severity-dist">
                {severityDist?.distribution?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={severityDist.distribution}
                        cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                        dataKey="count" nameKey="severity" label={({ severity, pct }) => `${(severity || "").replace(/_/g, " ")} (${pct}%)`}
                      >
                        {severityDist.distribution.map((d, i) => (
                          <Cell key={i} fill={SEVERITY_COLORS_MAP[d.severity] || CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </ChartPanel>

              {/* Clause Heat Map */}
              <ChartPanel title={t('platform:analytics.clauseHeatmap')} icon={Flame} color="text-red-400" id="clause-heatmap">
                {clauseHeatmap?.clauses?.length > 0 ? (
                  <div className="space-y-2 max-h-[280px] overflow-y-auto pr-2">
                    {clauseHeatmap.clauses.map((c, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0`} style={{ backgroundColor: HEAT_COLORS[c.heat] }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-mono text-xs truncate">{c.clause}</div>
                          <div className="text-[#8B95A5] text-xs">{c.finding_count} findings / {c.audit_count} audits</div>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="bg-[#181B23] rounded px-2 py-0.5 text-xs font-semibold" style={{ color: HEAT_COLORS[c.heat] }}>
                            {c.weighted_score}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <EmptyChart message={t('platform:analytics.noClauseFindings')} />}
              </ChartPanel>
            </div>

            {/* Recurrence Trend */}
            <ChartPanel title={t('platform:analytics.recurrenceTrend')} icon={Activity} color="text-purple-400" id="recurrence-trend">
              {recurrenceTrend?.overall?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={recurrenceTrend.overall}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2A2D38" />
                    <XAxis dataKey="period" tick={{ fill: "#8B95A5", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#8B95A5", fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="count" stroke="#A855F7" strokeWidth={2} dot={{ r: 3, fill: "#A855F7" }} name={t('platform:analytics.signals')} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <EmptyChart message={t('platform:analytics.noRecurrenceSignals')} />}
            </ChartPanel>
          </>
        )}

        {(section === "all" || section === "vendor") && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Vendor Status Distribution */}
            <ChartPanel title={t('platform:analytics.vendorStatus')} icon={Building2} color="text-emerald-400" id="vendor-status">
              {vendorTrends && Object.keys(vendorTrends.status_distribution || {}).length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={Object.entries(vendorTrends.status_distribution).map(([k, v]) => ({ status: k, count: v }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2A2D38" />
                    <XAxis dataKey="status" tick={{ fill: "#8B95A5", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#8B95A5", fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Vendors" radius={[4, 4, 0, 0]}>
                      {Object.entries(vendorTrends.status_distribution).map(([k], i) => (
                        <Cell key={i} fill={STATUS_COLORS[k] || CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyChart message="No vendor data yet." />}
            </ChartPanel>

            {/* Vendor Risk Distribution */}
            <ChartPanel title="Vendor Risk Profile" icon={Shield} color="text-orange-400" id="vendor-risk">
              {vendorTrends && Object.keys(vendorTrends.risk_distribution || {}).length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={Object.entries(vendorTrends.risk_distribution).map(([k, v]) => ({ name: k, value: v }))}
                      cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {Object.entries(vendorTrends.risk_distribution).map(([k], i) => (
                        <Cell key={i} fill={HEAT_COLORS[k] || CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <EmptyChart message="No vendor risk profiles yet." />}
            </ChartPanel>
          </div>
        )}

        {(section === "all" || section === "compliance") && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Redaction Metrics */}
            <ChartPanel title="Redaction Job Metrics" icon={Eye} color="text-red-400" id="redaction-metrics">
              {redactionMetrics && (redactionMetrics.total_jobs > 0 || (redactionMetrics.entity_frequency || []).length > 0) ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-[#181B23] rounded p-3">
                      <div className="text-white font-semibold text-lg">{redactionMetrics.total_jobs}</div>
                      <div className="text-[#8B95A5] text-xs">Total Jobs</div>
                    </div>
                    {Object.entries(redactionMetrics.status_distribution || {}).map(([k, v]) => (
                      <div key={k} className="bg-[#181B23] rounded p-3">
                        <div className="text-white font-semibold text-lg">{v}</div>
                        <div className="text-[#8B95A5] text-xs capitalize">{k}</div>
                      </div>
                    ))}
                  </div>
                  {(redactionMetrics.entity_frequency || []).length > 0 && (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={redactionMetrics.entity_frequency.slice(0, 8)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#2A2D38" />
                        <XAxis type="number" tick={{ fill: "#8B95A5", fontSize: 11 }} />
                        <YAxis dataKey="entity_type" type="category" tick={{ fill: "#8B95A5", fontSize: 10 }} width={100} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" fill="#EF4444" name="Detected" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              ) : <EmptyChart message="No redaction jobs yet." />}
            </ChartPanel>

            {/* Disclosure Compliance */}
            <ChartPanel title="Disclosure Acceptance Compliance" icon={FileText} color="text-amber-400" id="disclosure-compliance">
              {disclosureCompliance && (disclosureCompliance.total_templates > 0 || disclosureCompliance.total_acceptances > 0) ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="bg-[#181B23] rounded p-3 text-center">
                      <div className="text-white font-semibold text-lg">{disclosureCompliance.total_templates}</div>
                      <div className="text-[#8B95A5] text-xs">Templates</div>
                    </div>
                    <div className="bg-[#181B23] rounded p-3 text-center">
                      <div className="text-emerald-400 font-semibold text-lg">{disclosureCompliance.active_templates}</div>
                      <div className="text-[#8B95A5] text-xs">Active</div>
                    </div>
                    <div className="bg-[#181B23] rounded p-3 text-center">
                      <div className="text-[#36A9E1] font-semibold text-lg">{disclosureCompliance.total_acceptances}</div>
                      <div className="text-[#8B95A5] text-xs">Acceptances</div>
                    </div>
                  </div>
                  {Object.keys(disclosureCompliance.acceptance_by_workflow || {}).length > 0 && (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={Object.entries(disclosureCompliance.acceptance_by_workflow).map(([k, v]) => ({ workflow: k, count: v }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2A2D38" />
                        <XAxis dataKey="workflow" tick={{ fill: "#8B95A5", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#8B95A5", fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" fill="#36A9E1" name="Acceptances" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              ) : <EmptyChart message="No disclosure data yet." />}
            </ChartPanel>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default AnalyticsChartsPage;
